import React from 'react';
import { motion } from 'framer-motion';
import { Label } from '../ui/label';
import { BookingTile } from './BookingTile';

const fade = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 },
  transition: { duration: 0.25 },
};

const BEDROOMS = ['1', '2', '3', '4', '5+'];
const BATHROOMS = ['1', '2', '3', '4+'];
const SQ_FT = [
  { id: 'under_1000', label: 'Under 1,000 sq ft' },
  { id: '1000_2000', label: '1,000 – 2,000' },
  { id: '2000_3000', label: '2,000 – 3,000' },
  { id: '3000_plus', label: '3,000+' },
];
const CARPET_ROOMS = ['1', '2', '3', '4', '5+'];
const STAIN_OPTIONS = [
  { id: 'light', label: 'Light / None' },
  { id: 'moderate', label: 'Moderate' },
  { id: 'heavy', label: 'Heavy' },
];

export const HomeStep = ({
  isCarpet,
  bedrooms,
  setBedrooms,
  bathrooms,
  setBathrooms,
  sqFt,
  setSqFt,
  carpetRooms,
  setCarpetRooms,
  carpetSqFt,
  setCarpetSqFt,
  stainSeverity,
  setStainSeverity,
}) => (
  <motion.div
    key="s2"
    {...fade}
    className="bg-white rounded-2xl p-6 shadow-sm border border-zinc-100"
    data-testid="step-home"
  >
    <h2 className="text-lg sm:text-xl font-bold text-zinc-900 font-heading mb-1">
      {isCarpet ? 'Carpet Details' : 'Home Details'}
    </h2>
    <p className="text-sm text-zinc-500 mb-5">
      {isCarpet ? 'Tell us about your carpets' : 'Tell us about your space'}
    </p>

    {isCarpet ? (
      <div className="space-y-5">
        <div>
          <Label className="text-sm font-semibold text-zinc-700 mb-3 block">
            Number of Carpeted Rooms
          </Label>
          <div className="flex flex-wrap gap-2" data-testid="carpet-rooms-selector">
            {CARPET_ROOMS.map((r) => (
              <BookingTile
                key={r}
                active={carpetRooms === r}
                onClick={() => setCarpetRooms(r)}
                testId={`carpet-room-${r}`}
              >
                {r}
              </BookingTile>
            ))}
          </div>
        </div>
        <div>
          <Label className="text-sm font-semibold text-zinc-700 mb-3 block">
            Approximate Square Footage
          </Label>
          <div className="grid grid-cols-2 gap-2" data-testid="carpet-sqft-selector">
            {SQ_FT.map((s) => (
              <BookingTile
                key={s.id}
                active={carpetSqFt === s.id}
                onClick={() => setCarpetSqFt(s.id)}
                testId={`carpet-sqft-${s.id}`}
              >
                {s.label}
              </BookingTile>
            ))}
          </div>
        </div>
        <div>
          <Label className="text-sm font-semibold text-zinc-700 mb-3 block">
            Stain Severity (optional)
          </Label>
          <div className="flex flex-wrap gap-2" data-testid="stain-selector">
            {STAIN_OPTIONS.map((s) => (
              <BookingTile
                key={s.id}
                active={stainSeverity === s.id}
                onClick={() => setStainSeverity(s.id)}
                testId={`stain-${s.id}`}
              >
                {s.label}
              </BookingTile>
            ))}
          </div>
          {stainSeverity !== 'light' && (
            <p className="text-xs text-amber-600 mt-2">Stain treatment adds $25/room</p>
          )}
        </div>
      </div>
    ) : (
      <div className="space-y-5">
        <div>
          <Label className="text-sm font-semibold text-zinc-700 mb-3 block">Bedrooms</Label>
          <div className="flex flex-wrap gap-2" data-testid="bedroom-selector">
            {BEDROOMS.map((b) => (
              <BookingTile
                key={b}
                active={bedrooms === b}
                onClick={() => setBedrooms(b)}
                testId={`bed-${b}`}
              >
                {b}
              </BookingTile>
            ))}
          </div>
        </div>
        <div>
          <Label className="text-sm font-semibold text-zinc-700 mb-3 block">Bathrooms</Label>
          <div className="flex flex-wrap gap-2" data-testid="bathroom-selector">
            {BATHROOMS.map((b) => (
              <BookingTile
                key={b}
                active={bathrooms === b}
                onClick={() => setBathrooms(b)}
                testId={`bath-${b}`}
              >
                {b}
              </BookingTile>
            ))}
          </div>
        </div>
        <div>
          <Label className="text-sm font-semibold text-zinc-700 mb-3 block">Home Size</Label>
          <div className="grid grid-cols-2 gap-2" data-testid="sqft-selector">
            {SQ_FT.map((s) => (
              <BookingTile
                key={s.id}
                active={sqFt === s.id}
                onClick={() => setSqFt(s.id)}
                testId={`sqft-${s.id}`}
              >
                {s.label}
              </BookingTile>
            ))}
          </div>
        </div>
      </div>
    )}
  </motion.div>
);
